import type { Distance, Id } from "@hisani/core";
import {
  STOPWATCH_SECTIONAL_DISTANCES_METERS,
  calculateAverageSpeedMetersPerSecond,
  calculateCumulativeDistanceMeters,
  createStopwatchRun,
  formatElapsedToHundredths,
  getElapsedMilliseconds,
  type Sectional,
  type StopwatchRun,
} from "@hisani/stopwatch";
import { type useTranslations } from "next-intl";
import type {
  CompletedStopwatchSession,
  PersistedTimerSnapshot,
} from "./stopwatch-storage";
import { browserClock } from "./stopwatch-browser";

export interface TimerViewModel {
  readonly run: StopwatchRun;
  readonly nextSplitDistanceMeters: number;
}

type TimerWithSectionals = {
  readonly run: Pick<StopwatchRun, "sectionals">;
};

export type PendingConfirmation =
  | { readonly kind: "reset"; readonly timerId: string }
  | { readonly kind: "finish" }
  | {
      readonly kind: "delete";
      readonly session: CompletedStopwatchSession;
    };

export const DISTANCE_OPTIONS = STOPWATCH_SECTIONAL_DISTANCES_METERS.map(
  (meters) => ({ furlongs: meters / 200, meters }),
);

export function createTimer(id: Id): TimerViewModel {
  return {
    run: createStopwatchRun(id),
    nextSplitDistanceMeters: 200,
  };
}

export function coveredDistanceMeters(timer: TimerWithSectionals): number {
  return calculateCumulativeDistanceMeters(timer.run.sectionals);
}

export function calculateSpeed(
  timer: TimerWithSectionals,
  elapsedMilliseconds: number,
): number | null {
  return calculateMetersPerSecond(
    coveredDistanceMeters(timer),
    elapsedMilliseconds,
  );
}

export function calculateSectionalSpeed(
  distanceMeters: number,
  sectionalMilliseconds: number,
): number | null {
  return calculateMetersPerSecond(distanceMeters, sectionalMilliseconds);
}

export function formatAverageSpeed(
  metersPerSecond: number,
  locale: string,
  t: ReturnType<typeof useTranslations<"Stopwatch">>,
): string {
  const values = formatAverageSpeedValues(metersPerSecond, locale);
  return t("kilometersAndMilesPerHour", values);
}

export function formatAverageSpeedValues(
  metersPerSecond: number,
  locale: string,
): {
  readonly kilometersPerHour: string;
  readonly milesPerHour: string;
} {
  const numberFormatter = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });
  return {
    kilometersPerHour: numberFormatter.format(metersPerSecond * 3.6),
    milesPerHour: numberFormatter.format(metersPerSecond * 2.236_936_292_054_4),
  };
}

function calculateMetersPerSecond(
  distanceMeters: number,
  elapsedMilliseconds: number,
): number | null {
  if (elapsedMilliseconds <= 0) {
    return null;
  }

  const distance: Distance = { value: distanceMeters, unit: "METERS" };
  return calculateAverageSpeedMetersPerSecond(distance, elapsedMilliseconds);
}

export function toPersistedTimers(
  timers: readonly TimerViewModel[],
): readonly PersistedTimerSnapshot[] {
  return timers.map((timer) => toPersistedTimer(timer));
}

export function toPersistedTimer(
  timer: TimerViewModel,
  comment: string | null = null,
): PersistedTimerSnapshot {
  const elapsedMilliseconds = getElapsedMilliseconds(timer.run, browserClock);
  const status = timer.run.status === "running" ? "paused" : timer.run.status;

  return {
    comment,
    run: {
      id: timer.run.id,
      label: timer.run.label,
      status,
      elapsedMilliseconds,
      sectionals: timer.run.sectionals,
    },
  };
}

export function statusMessage(
  status: StopwatchRun["status"],
  t: ReturnType<typeof useTranslations<"Stopwatch">>,
): string {
  switch (status) {
    case "idle":
      return t("statusIdle");
    case "running":
      return t("statusRunning");
    case "paused":
      return t("statusPaused");
    case "stopped":
      return t("statusStopped");
  }
}

export function buildSummary(
  timers: readonly PersistedTimerSnapshot[],
  locale: string,
  t: ReturnType<typeof useTranslations<"Stopwatch">>,
  {
    dateEpochMilliseconds,
    firstTimerNumber = 1,
  }: {
    readonly dateEpochMilliseconds: number;
    readonly firstTimerNumber?: number;
  },
): string {
  const lines = [t("summaryTitle")];
  const date = new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
    dateEpochMilliseconds,
  );

  for (const [index, timer] of timers.entries()) {
    const elapsed = timer.run.elapsedMilliseconds;
    const label =
      timer.run.label?.trim() ||
      t("timerDefault", { number: index + firstTimerNumber });
    lines.push(
      "",
      label,
      date,
      "",
      t(timer.run.status === "stopped" ? "summaryFinal" : "summaryElapsed", {
        time: formatElapsedToHundredths(elapsed),
      }),
    );
    const speed = calculateSpeed(timer, elapsed);
    if (speed !== null) {
      lines.push(t("summarySpeed", formatAverageSpeedValues(speed, locale)));
    }
    if (timer.comment !== null) {
      lines.push(t("summaryComment", { comment: timer.comment }));
    }
    if (timer.run.sectionals.length === 0) continue;

    lines.push("", t("summarySplits"));
    for (const [sectionalIndex, sectional] of timer.run.sectionals.entries()) {
      const cumulativeDistanceMeters = calculateCumulativeDistanceMeters(
        timer.run.sectionals.slice(0, sectionalIndex + 1),
      );
      lines.push(
        t("summarySectional", {
          number: sectional.sequence,
          cumulativeDistance: formatSplitDistance(
            cumulativeDistanceMeters,
            locale,
            t,
          ),
          cumulativeTime: formatElapsedToHundredths(
            sectional.cumulativeMilliseconds,
          ),
          sectionDistance: formatSplitDistance(
            sectional.distanceMeters,
            locale,
            t,
          ),
          sectionalTime: formatElapsedToHundredths(sectional.splitMilliseconds),
        }),
      );
    }
  }

  return lines.join("\n");
}

export function formatSplitDistance(
  distanceMeters: Sectional["distanceMeters"],
  locale: string,
  t: ReturnType<typeof useTranslations<"Stopwatch">>,
): string {
  return t("distanceFurlongShort", {
    value: new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(distanceMeters / 200),
  });
}

export function getConfirmationCopy(
  confirmation: PendingConfirmation | null,
  t: ReturnType<typeof useTranslations<"Stopwatch">>,
): {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
} | null {
  if (confirmation === null) {
    return null;
  }
  switch (confirmation.kind) {
    case "reset":
      return {
        title: t("resetDialogTitle"),
        description: t("resetConfirm"),
        confirmLabel: t("reset"),
      };
    case "finish":
      return {
        title: t("finishDialogTitle"),
        description: t("finishRunningConfirm"),
        confirmLabel: t("finishSession"),
      };
    case "delete":
      return {
        title: t("deleteDialogTitle"),
        description: t("deleteSessionConfirm"),
        confirmLabel: t("delete"),
      };
  }
}
